import React, { useRef, useEffect, useMemo } from 'react';

/** Magnifying glass (uses currentColor) — zoom / hover-enlarge affordance. */
export function MagnifyIcon({ className }) {
  return (
    <svg
      className={className}
      width={28}
      height={28}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="10" cy="10" r="6.25" stroke="currentColor" strokeWidth="2.2" />
      <path
        d="M14.2 14.2 20 20"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Universal access mark: person with outstretched limbs inside a circle (uses currentColor). */
function UniversalAccessSymbol({ className }) {
  return (
    <svg
      className={className}
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.65" />
      <circle cx="12" cy="7" r="1.55" fill="currentColor" />
      <path
        d="M12 8.85v4.75M8.15 11.35h7.7M12 13.65 9.15 19M12 13.65l2.85 5.35"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Colour-scheme picker.
 * - Header: in-flow; icon + label; panel absolutely below trigger.
 * - Splash + language: optional `splashLangSelect` + `ifip-a11y-control-root--splash-inline-toolbar`
 *   keeps icon-only trigger beside the language control in page flow (splash footer).
 *   With `splashLangSelect` only + `--splash-bar` + fixed `ifip-splash-chrome` (legacy fixed corner).
 * - Standalone splash (`--splash` without `splashLangSelect`): fixed corner, text trigger.
 * - Portal header: optional `showPortalHoverZoomToggle` + `hoverZoomOthers` / `onHoverZoomOthersChange`
 *   to gate hover zoom on other school-portal controls (stored with colour prefs).
 */
export function AccessibilityControls({
  panelOpen,
  setPanelOpen,
  scheme,
  setScheme,
  labels,
  rootClassName = 'ifip-a11y-control-root ifip-a11y-control-root--splash',
  panelId = 'a11y-panel',
  splashLangSelect = null,
  showPortalHoverZoomToggle = false,
  hoverZoomOthers = false,
  onHoverZoomOthersChange = null,
  registerFontScaleMode = false,
  registerFontScale = 1,
  onRegisterFontScaleChange = null,
  accessibilityTriggerIconOnly = false,
}) {
  const rootRef = useRef(null);

  const opts = useMemo(() => {
    const base = [
      { id: 'default', label: labels.schemeDefault },
      { id: 'cg', label: labels.schemeCg },
      { id: 'wb', label: labels.schemeWb },
    ];
    if (labels.schemeNb) base.push({ id: 'nb', label: labels.schemeNb });
    if (labels.schemeSol) base.push({ id: 'sol', label: labels.schemeSol });
    return base;
  }, [labels]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    const onPointerDown = (e) => {
      const root = rootRef.current;
      if (!root || root.contains(e.target)) return;
      setPanelOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [panelOpen, setPanelOpen]);

  const isHeaderVariant = rootClassName.includes('ifip-a11y-control-root--header');
  const isSplashInlineToolbar = rootClassName.includes('ifip-a11y-control-root--splash-inline-toolbar');
  const isSplashLangRow = Boolean(splashLangSelect);
  const rootClassMerged = [
    rootClassName,
    isSplashLangRow && !isSplashInlineToolbar ? 'ifip-splash-chrome' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const splashLangRowClass = isSplashInlineToolbar ? 'ifip-splash-inline-row' : 'ifip-splash-chrome-row';

  const triggerClassName = [
    'ifip-a11y-trigger-btn',
    isHeaderVariant || isSplashLangRow ? 'ifip-a11y-trigger-btn--icon-only' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const panelAriaLabel =
    (showPortalHoverZoomToggle && labels.panelAriaSplit) ||
    (registerFontScaleMode && labels.panelAriaSplit)
      ? labels.panelAriaSplit
      : labels.panelAria;

  const portalZoomBlock =
    showPortalHoverZoomToggle && onHoverZoomOthersChange && labels.hoverZoomOthersLabel ? (
      <>
        <div className="ifip-a11y-panel__section ifip-a11y-panel__section--zoom">
          <div className="ifip-a11y-panel__zoom-head">
            <MagnifyIcon className="ifip-a11y-panel__zoom-icon" />
            <p className="ifip-a11y-panel__section-title ifip-a11y-panel__section-title--zoom">
              {labels.zoomSectionTitle || 'Zoom'}
            </p>
          </div>
          <div className="ifip-a11y-panel__zoom-block">
            <label className="ifip-a11y-panel__toggle" htmlFor={`${panelId}-hover-zoom-others`}>
              <input
                id={`${panelId}-hover-zoom-others`}
                type="checkbox"
                checked={Boolean(hoverZoomOthers)}
                onChange={(e) => onHoverZoomOthersChange(e.target.checked)}
                aria-describedby={
                  labels.hoverZoomOthersHint ? `${panelId}-hover-zoom-hint` : undefined
                }
              />
              <span className="ifip-a11y-panel__toggle-text">{labels.hoverZoomOthersLabel}</span>
            </label>
            {labels.hoverZoomOthersHint ? (
              <p id={`${panelId}-hover-zoom-hint`} className="ifip-a11y-panel__hint">
                {labels.hoverZoomOthersHint}
              </p>
            ) : null}
          </div>
        </div>
        <div className="ifip-a11y-panel__section ifip-a11y-panel__section--scheme">
          <p className="ifip-a11y-panel__section-title">{labels.panelTitle}</p>
          <div className="ifip-a11y-panel__options">
            {opts.map((opt) => (
              <button
                key={opt.id}
                type="button"
                aria-pressed={scheme === opt.id}
                onClick={() => setScheme(opt.id)}
                className={
                  scheme === opt.id ? 'ifip-a11y-option ifip-a11y-option--active' : 'ifip-a11y-option'
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </>
    ) : null;

  const clampRegisterFont = (s) => Math.min(1.5, Math.max(0.75, Math.round(s * 100) / 100));

  const registerFontScaleBlock =
    registerFontScaleMode && typeof onRegisterFontScaleChange === 'function' ? (
      <>
        <div className="ifip-a11y-panel__section ifip-a11y-panel__section--zoom">
          <div className="ifip-a11y-panel__zoom-head">
            <MagnifyIcon className="ifip-a11y-panel__zoom-icon" />
            <p className="ifip-a11y-panel__section-title ifip-a11y-panel__section-title--zoom">
              {labels.fontScaleSectionTitle || 'Text size'}
            </p>
          </div>
          <div
            className="ifip-a11y-panel__zoom-block ifip-a11y-panel__font-scale-row"
            role="group"
            aria-label={labels.fontScaleGroupAria || labels.fontScaleSectionTitle || 'Text size'}
          >
            <button
              type="button"
              className="ifip-a11y-font-scale-btn"
              disabled={registerFontScale <= 0.75}
              onClick={() => onRegisterFontScaleChange(clampRegisterFont(registerFontScale - 0.05))}
              aria-label={labels.fontScaleSmaller || 'Decrease text size'}
            >
              −
            </button>
            <span className="ifip-a11y-font-scale-readout" aria-live="polite">
              {Math.round(registerFontScale * 100)}%
            </span>
            <button
              type="button"
              className="ifip-a11y-font-scale-btn"
              disabled={registerFontScale >= 1.5}
              onClick={() => onRegisterFontScaleChange(clampRegisterFont(registerFontScale + 0.05))}
              aria-label={labels.fontScaleLarger || 'Increase text size'}
            >
              +
            </button>
          </div>
        </div>
        <div className="ifip-a11y-panel__section ifip-a11y-panel__section--scheme">
          <p className="ifip-a11y-panel__section-title">{labels.panelTitle}</p>
          <div className="ifip-a11y-panel__options">
            {opts.map((opt) => (
              <button
                key={opt.id}
                type="button"
                aria-pressed={scheme === opt.id}
                onClick={() => setScheme(opt.id)}
                className={
                  scheme === opt.id ? 'ifip-a11y-option ifip-a11y-option--active' : 'ifip-a11y-option'
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </>
    ) : null;

  const schemeOnlyBlock = (
    <>
      <p className="ifip-a11y-panel__title">{labels.panelTitle}</p>
      <div className="ifip-a11y-panel__options">
        {opts.map((opt) => (
          <button
            key={opt.id}
            type="button"
            aria-pressed={scheme === opt.id}
            onClick={() => setScheme(opt.id)}
            className={scheme === opt.id ? 'ifip-a11y-option ifip-a11y-option--active' : 'ifip-a11y-option'}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div ref={rootRef} className={rootClassMerged}>
      {isSplashLangRow ? (
        <div className={splashLangRowClass}>
          {isSplashInlineToolbar ? splashLangSelect : null}
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            aria-expanded={panelOpen}
            aria-controls={panelId}
            className={triggerClassName}
            title={labels.btn}
            aria-label={labels.btn}
          >
            <UniversalAccessSymbol className="ifip-a11y-universal-access-icon" />
          </button>
          {!isSplashInlineToolbar ? splashLangSelect : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPanelOpen((v) => !v)}
          aria-expanded={panelOpen}
          aria-controls={panelId}
          className={triggerClassName}
          title={labels.btn}
          aria-label={labels.btn}
        >
          {isHeaderVariant ? (
            accessibilityTriggerIconOnly ? (
              <UniversalAccessSymbol className="ifip-a11y-universal-access-icon" />
            ) : (
              <>
                <UniversalAccessSymbol className="ifip-a11y-universal-access-icon" />
                <span className="ifip-a11y-trigger-label">{labels.btn}</span>
              </>
            )
          ) : (
            labels.btn
          )}
        </button>
      )}
      {panelOpen && (
        <section id={panelId} aria-label={panelAriaLabel} className="ifip-a11y-panel">
          {portalZoomBlock || registerFontScaleBlock || schemeOnlyBlock}
        </section>
      )}
    </div>
  );
}
